import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type RefObject,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { accountHue, api, type PublicAccount, type ThreadView } from '../lib/api';
import styles from './SearchBar.module.css';

/**
 * Search is a MODE, not a permanent box.
 *
 * Kiln has no search field until you ask for one: `/` swaps the split-tab row
 * for this full-width query field, and Esc (or the X) puts the tabs back along
 * with the scroll position the list had before. That is the measured Superhuman
 * behaviour, and it is why the top of their main column reads as a mail app
 * rather than as webmail.
 *
 * The component stays MOUNTED while collapsed rather than unmounting, because
 * the command palette reaches the field through the `mountedBars` registry —
 * an unmounted bar would make "Search mail" in the palette quietly dead. It
 * simply renders nothing until `active`.
 *
 * The field itself parses prose into visible, removable pills. Showing exactly
 * what the machine decided — and letting the user delete a wrong decision — is
 * the whole trust design; nothing is inferred invisibly.
 */

export type PillField =
  | 'from'
  | 'to'
  | 'subject'
  | 'has'
  | 'is'
  | 'in'
  | 'label'
  | 'account'
  | 'before'
  | 'after';

export type SearchPill = {
  id: string;
  field: PillField;
  value: string;
  /** 'user' pills come from deterministic client parsing, 'ai' from the model. */
  source: 'user' | 'ai';
  accountId?: number;
};

export type SearchStatus = 'idle' | 'loading' | 'ready' | 'error';

export type SearchState = {
  active: boolean;
  raw: string;
  query: string;
  pills: SearchPill[];
  accountId: number | null;
  exact: ThreadView[];
  semantic: ThreadView[];
  status: SearchStatus;
  semanticStatus: SearchStatus;
};

export type SearchBarHandle = {
  focus: (allAccounts?: boolean) => void;
  /** Focus the field and run a query, e.g. a recent search from the palette. */
  search: (query: string) => void;
  close: () => void;
};

/**
 * '/' is a shell-level key, but the field it focuses lives in the list pane.
 * Mounted bars register here so the dispatcher can reach the innermost one
 * without the shell having to hold a ref.
 */
const mountedBars: SearchBarHandle[] = [];

/** Returns false when no search field is mounted, so the caller can fall back. */
export function focusMailSearch(allAccounts?: boolean): boolean {
  const bar = mountedBars[mountedBars.length - 1];
  if (!bar) return false;
  bar.focus(allAccounts);
  return true;
}

/** Same contract as focusMailSearch, but seeds the field with a query. */
export function runMailSearch(query: string): boolean {
  const bar = mountedBars[mountedBars.length - 1];
  if (!bar) return false;
  bar.search(query);
  return true;
}

export type SearchBarProps = {
  aiEnabled: boolean;
  accounts: PublicAccount[];
  /** Current account scope; used as the default `account:` pill. */
  accountFilter: number | null;
  onResults: (state: SearchState) => void;
  /**
   * Fired SYNCHRONOUSLY as the mode opens and closes, so the host can swap the
   * tab row out in the same commit the field appears in. `onResults` is an
   * effect and lands one paint later, which would flash both rows at once.
   */
  onActiveChange?: (active: boolean) => void;
  /** Called after Esc has restored the previous scroll position. */
  onExit?: () => void;
  /** The list pane scroller, so Esc can put the user back exactly where they were. */
  scrollRef?: RefObject<HTMLElement | null>;
  placeholder?: string;
};

/** Convenient initial value for whoever owns the results. */
export const EMPTY_SEARCH_STATE: SearchState = {
  active: false,
  raw: '',
  query: '',
  pills: [],
  accountId: null,
  exact: [],
  semantic: [],
  status: 'idle',
  semanticStatus: 'idle',
};

/* ----------------------------- recent searches ---------------------------- */

const RECENTS_KEY = 'kiln.recent-searches';
const RECENTS_MAX = 12;

export function getRecentSearches(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

export function pushRecentSearch(query: string): void {
  const trimmed = query.trim();
  if (trimmed === '' || typeof localStorage === 'undefined') return;
  const next = [trimmed, ...getRecentSearches().filter((q) => q !== trimmed)].slice(0, RECENTS_MAX);
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // A full or disabled localStorage must never break searching.
  }
}

/* --------------------------------- parsing -------------------------------- */

const FIELDS = new Set<PillField>([
  'from',
  'to',
  'subject',
  'has',
  'is',
  'in',
  'label',
  'account',
  'before',
  'after',
]);

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

function toISODate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function shiftDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toISODate(date);
}

/** Turns the date words people actually type into an ISO day. */
export function parseDateish(input: string): string | null {
  const text = input.trim().toLowerCase();
  if (text === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (text === 'today') return toISODate(new Date());
  if (text === 'yesterday') return shiftDays(-1);
  if (text === 'tomorrow') return shiftDays(1);

  const relative = /^(?:last|past)\s+(day|week|month|year)$/.exec(text);
  if (relative) {
    const unit = relative[1];
    if (unit === 'day') return shiftDays(-1);
    if (unit === 'week') return shiftDays(-7);
    if (unit === 'month') return shiftDays(-30);
    return shiftDays(-365);
  }

  const counted = /^(\d+)\s+(day|week|month|year)s?\s+ago$/.exec(text);
  if (counted) {
    const amount = Number(counted[1]);
    const unit = counted[2];
    const perUnit = unit === 'day' ? 1 : unit === 'week' ? 7 : unit === 'month' ? 30 : 365;
    return shiftDays(-amount * perUnit);
  }

  const month = /^([a-z]+)(?:\s+(\d{1,2}))?(?:,?\s+(\d{4}))?$/.exec(text);
  if (month) {
    const index = MONTHS.findIndex((name) => name.startsWith(month[1] ?? '') && (month[1] ?? '').length >= 3);
    if (index >= 0) {
      const year = month[3] ? Number(month[3]) : new Date().getFullYear();
      const day = month[2] ? Number(month[2]) : 1;
      return toISODate(new Date(year, index, day));
    }
  }

  const slashed = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(text);
  if (slashed) {
    const year = Number(slashed[3]) < 100 ? 2000 + Number(slashed[3]) : Number(slashed[3]);
    return toISODate(new Date(year, Number(slashed[1]) - 1, Number(slashed[2])));
  }

  return null;
}

let pillSeq = 0;

function makePill(field: PillField, value: string, source: 'user' | 'ai', accountId?: number): SearchPill {
  return { id: `pill-${pillSeq++}`, field, value, source, accountId };
}

function matchAccount(term: string, accounts: PublicAccount[]): PublicAccount | undefined {
  const needle = term.toLowerCase();
  return accounts.find((account) => {
    const email = account.email.toLowerCase();
    const name = (account.displayName || '').toLowerCase();
    return email === needle || email.split('@')[0] === needle || name === needle || name.split(' ')[0] === needle;
  });
}

type ProseRule = {
  re: RegExp;
  build: (match: RegExpExecArray, accounts: PublicAccount[]) => SearchPill | null;
};

const PROSE_RULES: ProseRule[] = [
  {
    re: /\bfrom\s+((?:[\w.+-]+@[\w.-]+\.\w+)|[\w.'-]{2,})\b/i,
    build: (m) => makePill('from', m[1] ?? '', 'user'),
  },
  {
    re: /\b(?:to|sent\s+to)\s+((?:[\w.+-]+@[\w.-]+\.\w+)|[\w.'-]{2,})\b/i,
    build: (m) => makePill('to', m[1] ?? '', 'user'),
  },
  {
    re: /\b(?:with|has|having|containing)\s+(?:an?\s+)?attachments?\b/i,
    build: () => makePill('has', 'attachment', 'user'),
  },
  { re: /\bunread\b/i, build: () => makePill('is', 'unread', 'user') },
  { re: /\b(?:starred|flagged)\b/i, build: () => makePill('is', 'starred', 'user') },
  {
    re: /\b(?:after|since|newer\s+than)\s+([\w\s/,-]{3,24}?)(?=\s|$)/i,
    build: (m) => {
      const iso = parseDateish(m[1] ?? '');
      return iso ? makePill('after', iso, 'user') : null;
    },
  },
  {
    re: /\b(?:before|until|older\s+than)\s+([\w\s/,-]{3,24}?)(?=\s|$)/i,
    build: (m) => {
      const iso = parseDateish(m[1] ?? '');
      return iso ? makePill('before', iso, 'user') : null;
    },
  },
  {
    re: /\b(?:last|past)\s+(?:week|month|year)\b/i,
    build: (m) => {
      const iso = parseDateish(m[0] ?? '');
      return iso ? makePill('after', iso, 'user') : null;
    },
  },
  {
    re: /\b(?:in|on)\s+(?:my\s+)?([\w.'-]{2,})\s+(?:account|inbox|mailbox)\b/i,
    build: (m, accounts) => {
      const account = matchAccount(m[1] ?? '', accounts);
      return account ? makePill('account', account.email, 'user', account.id) : null;
    },
  },
];

export type ParseResult = { pills: SearchPill[]; rest: string };

/**
 * Deterministic client parse. Only text the user has already finished (up to
 * the last space) is examined, so pills never form under a half-typed word.
 */
export function parseSearchInput(text: string, accounts: PublicAccount[]): ParseResult {
  let rest = text;
  const pills: SearchPill[] = [];

  const explicit = /(\w+):("[^"]*"|[^\s]+)/g;
  let match = explicit.exec(rest);
  while (match !== null) {
    const field = (match[1] ?? '').toLowerCase();
    const raw = (match[2] ?? '').replace(/^"|"$/g, '');
    if (FIELDS.has(field as PillField) && raw !== '') {
      const typed = field as PillField;
      if (typed === 'account') {
        const account = matchAccount(raw, accounts);
        pills.push(makePill('account', account?.email ?? raw, 'user', account?.id));
      } else if (typed === 'before' || typed === 'after') {
        pills.push(makePill(typed, parseDateish(raw) ?? raw, 'user'));
      } else {
        pills.push(makePill(typed, raw, 'user'));
      }
      rest = `${rest.slice(0, match.index)}${rest.slice(match.index + match[0].length)}`;
      explicit.lastIndex = match.index;
    }
    match = explicit.exec(rest);
  }

  for (const rule of PROSE_RULES) {
    const hit = rule.re.exec(rest);
    if (!hit) continue;
    const pill = rule.build(hit, accounts);
    if (!pill) continue;
    pills.push(pill);
    rest = `${rest.slice(0, hit.index)}${rest.slice(hit.index + hit[0].length)}`;
  }

  return { pills, rest: rest.replace(/\s+/g, ' ').trim() };
}

function pillText(pill: SearchPill): string {
  return `${pill.field}:${pill.value}`;
}

function buildQuery(pills: SearchPill[], rest: string): string {
  const parts = pills
    .filter((pill) => pill.field !== 'account')
    .map((pill) => (/\s/.test(pill.value) ? `${pill.field}:"${pill.value}"` : pillText(pill)));
  if (rest.trim() !== '') parts.push(rest.trim());
  return parts.join(' ');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The AI filter bag is loosely typed by contract; accept only what we can show. */
function filtersToPills(input: unknown, accounts: PublicAccount[]): SearchPill[] {
  if (!isRecord(input)) return [];
  const pills: SearchPill[] = [];
  for (const [key, value] of Object.entries(input)) {
    const field = key.toLowerCase();
    if (!FIELDS.has(field as PillField)) continue;
    if (value === null || value === undefined || value === false) continue;
    const text = typeof value === 'string' ? value : String(value);
    if (text.trim() === '') continue;
    const typed = field as PillField;
    if (typed === 'account') {
      const account = matchAccount(text, accounts);
      pills.push(makePill('account', account?.email ?? text, 'ai', account?.id));
    } else if (typed === 'before' || typed === 'after') {
      pills.push(makePill(typed, parseDateish(text) ?? text, 'ai'));
    } else {
      pills.push(makePill(typed, text, 'ai'));
    }
  }
  return pills;
}

/* --------------------------------- divider -------------------------------- */

/** Rendered by the list between literal hits and model-suggested ones. */
export function SemanticDivider({ count }: { count?: number }): ReactElement {
  return (
    <div className={styles.semanticDivider} role="separator">
      <span className={styles.semanticLabel}>Related by meaning</span>
      {count !== undefined ? <span className={styles.semanticCount}>{count}</span> : null}
    </div>
  );
}

/* -------------------------------- component ------------------------------- */

export const SearchBar = forwardRef<SearchBarHandle, SearchBarProps>(function SearchBar(
  {
    aiEnabled,
    accounts,
    accountFilter,
    onResults,
    onActiveChange,
    onExit,
    scrollRef,
    placeholder = 'Search mail',
  },
  ref,
): ReactElement {
  const [active, setActive] = useState(false);
  const [text, setText] = useState('');
  const [pills, setPills] = useState<SearchPill[]>([]);
  const [aiPills, setAiPills] = useState<SearchPill[]>([]);
  const [aiRemainder, setAiRemainder] = useState('');
  const [debounced, setDebounced] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const savedScrollTop = useRef(0);
  const activeRef = useRef(false);
  activeRef.current = active;

  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;

  /** Announce the mode change without making every caller remember to. */
  const setMode = useCallback((next: boolean) => {
    setActive(next);
    onActiveChangeRef.current?.(next);
  }, []);

  // The field is display:none while collapsed, so it cannot take focus in the
  // same tick it is asked for. Focusing here — after the commit that reveals
  // it — is what makes `/` land in the field on the first keystroke.
  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);

  const accountPill = pills.find((pill) => pill.field === 'account');
  const accountId = accountPill?.accountId ?? (accountPill ? null : accountFilter);

  const query = useMemo(() => buildQuery(pills, text), [pills, text]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(timer);
  }, [query]);

  const exactEnabled = active && debounced !== '';

  const exact = useQuery({
    queryKey: ['search', 'exact', debounced, accountId],
    queryFn: () => api.search(debounced, accountId === null ? undefined : { account: accountId }),
    enabled: exactEnabled,
    staleTime: 30_000,
  });

  /* --- AI parse of whatever the deterministic pass could not claim --------- */
  const freeText = text.trim();
  useEffect(() => {
    if (!aiEnabled || !active || freeText.length < 3) {
      setAiPills([]);
      setAiRemainder('');
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void api.ai
        .parseSearch(freeText)
        .then((result) => {
          if (cancelled) return;
          setAiPills(filtersToPills(result.filters, accounts));
          setAiRemainder(typeof result.remainder === 'string' ? result.remainder : '');
        })
        .catch(() => {
          if (cancelled) return;
          setAiPills([]);
          setAiRemainder('');
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [aiEnabled, active, freeText, accounts]);

  const semanticQuery = useMemo(() => {
    if (!aiEnabled) return '';
    const built = buildQuery([...pills, ...aiPills], aiRemainder || freeText);
    return built === query ? '' : built;
  }, [aiEnabled, pills, aiPills, aiRemainder, freeText, query]);

  const semanticAccountId = aiPills.find((p) => p.field === 'account')?.accountId ?? accountId;

  // Exact results must never wait on the model: this is a separate query with
  // its own key, and the list renders whatever has arrived.
  const semantic = useQuery({
    queryKey: ['search', 'semantic', semanticQuery, semanticAccountId],
    queryFn: () =>
      api.search(semanticQuery, semanticAccountId === null ? undefined : { account: semanticAccountId }),
    enabled: aiEnabled && active && semanticQuery.trim() !== '',
    staleTime: 30_000,
  });

  const exactThreads = exact.data?.threads ?? [];
  const semanticThreads = useMemo(() => {
    const seen = new Set(exactThreads.map((thread) => thread.threadKey));
    return (semantic.data?.threads ?? []).filter((thread) => !seen.has(thread.threadKey));
  }, [exactThreads, semantic.data]);

  const status: SearchStatus = !exactEnabled
    ? 'idle'
    : exact.isError
      ? 'error'
      : exact.data === undefined
        ? 'loading'
        : 'ready';

  const semanticStatus: SearchStatus =
    !aiEnabled || semanticQuery === ''
      ? 'idle'
      : semantic.isError
        ? 'error'
        : semantic.data === undefined
          ? 'loading'
          : 'ready';

  const onResultsRef = useRef(onResults);
  onResultsRef.current = onResults;

  useEffect(() => {
    onResultsRef.current({
      active,
      raw: text,
      query: debounced,
      pills,
      accountId,
      exact: exactThreads,
      semantic: semanticThreads,
      status,
      semanticStatus,
    });
  }, [active, text, debounced, pills, accountId, exactThreads, semanticThreads, status, semanticStatus]);

  /* ------------------------------- behaviour ------------------------------ */

  const commit = useCallback(
    (raw: string) => {
      // Only text the user has finished (everything before the last space) is
      // eligible to become a pill, so words don't rearrange mid-keystroke.
      const boundary = raw.lastIndexOf(' ');
      if (boundary === -1) {
        setText(raw);
        return;
      }
      const head = raw.slice(0, boundary + 1);
      const tail = raw.slice(boundary + 1);
      const parsed = parseSearchInput(head, accounts);
      if (parsed.pills.length === 0) {
        setText(raw);
        return;
      }
      setPills((current) => {
        const merged = [...current];
        for (const pill of parsed.pills) {
          const existing = merged.findIndex((p) => p.field === pill.field && p.field !== 'from' && p.field !== 'to');
          if (existing >= 0) merged[existing] = pill;
          else merged.push(pill);
        }
        return merged;
      });
      setText(parsed.rest === '' ? tail : `${parsed.rest} ${tail}`);
    },
    [accounts],
  );

  const close = useCallback(() => {
    setMode(false);
    setText('');
    setPills([]);
    setAiPills([]);
    setAiRemainder('');
    inputRef.current?.blur();
    // Exiting the mode restores the list exactly where it was, which is the
    // half of "search is a mode" that people actually feel.
    const scroller = scrollRef?.current;
    if (scroller) scroller.scrollTop = savedScrollTop.current;
    onExit?.();
  }, [onExit, scrollRef, setMode]);

  const handle = useMemo<SearchBarHandle>(
    () => ({
      focus: (allAccounts?: boolean) => {
        if (!activeRef.current) savedScrollTop.current = scrollRef?.current?.scrollTop ?? 0;
        setMode(true);
        if (allAccounts) setPills((current) => current.filter((pill) => pill.field !== 'account'));
        else if (accountFilter !== null) {
          const account = accounts.find((a) => a.id === accountFilter);
          if (account) {
            setPills((current) =>
              current.some((pill) => pill.field === 'account')
                ? current
                : [makePill('account', account.email, 'user', account.id), ...current],
            );
          }
        }
        inputRef.current?.focus();
      },
      search: (query: string) => {
        if (!activeRef.current) savedScrollTop.current = scrollRef?.current?.scrollTop ?? 0;
        setMode(true);
        setPills([]);
        // The trailing space is what tells the parser the words are finished.
        commit(`${query} `);
        pushRecentSearch(query);
        inputRef.current?.focus();
      },
      close,
    }),
    [accountFilter, accounts, close, commit, scrollRef, setMode],
  );

  useImperativeHandle(ref, () => handle, [handle]);

  useEffect(() => {
    mountedBars.push(handle);
    return () => {
      const index = mountedBars.indexOf(handle);
      if (index >= 0) mountedBars.splice(index, 1);
    };
  }, [handle]);

  const acceptAiPills = useCallback(() => {
    setPills((current) => [...current, ...aiPills.map((pill) => ({ ...pill, source: 'user' as const }))]);
    setText(aiRemainder);
    setAiPills([]);
    setAiRemainder('');
  }, [aiPills, aiRemainder]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        commit(`${text} `);
        if (query.trim() !== '') pushRecentSearch(query.trim());
        return;
      }
      if (event.key === 'Backspace' && text === '' && pills.length > 0) {
        const input = event.currentTarget;
        if (input.selectionStart === 0 && input.selectionEnd === 0) {
          event.preventDefault();
          setPills((current) => current.slice(0, -1));
        }
        return;
      }
      if (event.key === 'Tab' && aiPills.length > 0) {
        event.preventDefault();
        acceptAiPills();
      }
    },
    [close, commit, text, query, pills.length, aiPills.length, acceptAiPills],
  );

  const removePill = (id: string) => setPills((current) => current.filter((pill) => pill.id !== id));

  const hueFor = (pill: SearchPill): string | undefined =>
    pill.accountId === undefined ? undefined : accountHue(pill.accountId, accounts);

  return (
    <div className={styles.dock} data-collapsed={!active}>
      <div
        className={styles.field}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) inputRef.current?.focus();
        }}
      >
        <svg className={styles.icon} viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>

        {pills.map((pill) => (
          <span key={pill.id} className={styles.pill}>
            {pill.accountId !== undefined ? (
              <span className={styles.pillDot} style={{ background: hueFor(pill) }} aria-hidden="true" />
            ) : null}
            <span className={styles.pillField}>{pill.field}</span>
            <span className={styles.pillValue}>{pill.value}</span>
            <button
              type="button"
              className={styles.pillRemove}
              aria-label={`Remove filter ${pillText(pill)}`}
              onClick={() => removePill(pill.id)}
            >
              <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true">
                <path d="M2 2l6 6M8 2l-6 6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          className={styles.input}
          value={text}
          placeholder={pills.length === 0 ? placeholder : ''}
          aria-label="Search mail"
          autoComplete="off"
          spellCheck={false}
          onFocus={() => {
            if (!active) savedScrollTop.current = scrollRef?.current?.scrollTop ?? 0;
            setMode(true);
          }}
          onChange={(event) => commit(event.target.value)}
          onKeyDown={onKeyDown}
        />

        {/* The X is the mouse's Esc: both exit the mode and restore the tabs. */}
        <button
          type="button"
          className={styles.escape}
          onClick={close}
          title="Close search · Esc"
          aria-label="Close search"
        >
          <kbd className={styles.kbd}>Esc</kbd>
          <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
            <path
              d="M2.5 2.5l7 7M9.5 2.5l-7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {aiEnabled && active && aiPills.length > 0 ? (
        <div className={styles.understood}>
          <span className={styles.understoodLabel}>Understood</span>
          {aiPills.map((pill) => (
            <span key={pill.id} className={`${styles.pill} ${styles.aiPill}`}>
              <span className={styles.pillField}>{pill.field}</span>
              <span className={styles.pillValue}>{pill.value}</span>
            </span>
          ))}
          <button type="button" className={styles.acceptAi} onClick={acceptAiPills}>
            Use these
            <kbd className={styles.kbd}>⇥</kbd>
          </button>
        </div>
      ) : null}
    </div>
  );
});

export default SearchBar;
